(function () {
  'use strict';

  const VAT_OPTION_NAME = 'VAT Exemption';
  const VAT_EXEMPT_VALUE = 'Yes';
  const VAT_STANDARD_VALUE = 'No';
  const VAT_STORAGE_KEY = 'vatExempt';
  const VAT_REASON_KEY = 'vatExemptReason';

  /** Per-cycle product cache — cleared at the start of each swap run. */
  const productCache = new Map();

  /**
   * Reads a selling plan id off a selling_plan_allocations entry, regardless
   * of whether it's shaped as a flat `selling_plan_id` (product/variant JSON,
   * e.g. /products/{handle}.js) or a nested `selling_plan.id` (cart line item
   * JSON, e.g. /cart.js). Returns null if there's no allocation or id.
   * @param {object|null|undefined} allocation
   * @returns {number|string|null}
   */
  function sellingPlanIdFromAllocation(allocation) {
    if (!allocation) return null;
    if (allocation.selling_plan_id != null) return allocation.selling_plan_id;
    if (allocation.selling_plan && allocation.selling_plan.id != null) return allocation.selling_plan.id;
    return null;
  }

  /**
   * Fetches /products/{handle}.js and caches by handle.
   * @param {string} handle
   * @returns {Promise<object>}
   */
  async function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    const res = await fetch('/products/' + encodeURIComponent(handle) + '.js');
    if (!res.ok) throw new Error('[VAT] Failed to fetch product "' + handle + '" (' + res.status + ')');
    const product = await res.json();
    productCache.set(handle, product);
    return product;
  }

  /**
   * Finds the counterpart variant — same selected options but with the target
   * VAT Exemption value. Returns null when:
   *   - the product has no "VAT Exemption" option
   *   - the line item is already on the correct side
   *   - no matching counterpart variant exists
   *
   * @param {object} product    Shopify storefront product JSON (/products/handle.js)
   * @param {number} variantId  Current variant ID
   * @param {string} targetVatValue  "Yes" or "No"
   * @returns {object|null}
   */
  function findCounterpartVariant(product, variantId, targetVatValue) {
    const vatIndex = product.options.findIndex(function (o) {
      return (typeof o === 'string' ? o : o.name) === VAT_OPTION_NAME;
    });
    if (vatIndex === -1) return null;

    const current = product.variants.find(function (v) { return v.id === variantId; });
    if (!current) return null;

    // Already on the correct side — nothing to do
    if (current.options[vatIndex] === targetVatValue) return null;

    // Match all options except VAT Exemption
    return product.variants.find(function (v) {
      return (
        v.options[vatIndex] === targetVatValue &&
        v.options.every(function (val, idx) {
          return idx === vatIndex || val === current.options[idx];
        })
      );
    }) || null;
  }

  /**
   * Iterates the current cart, swaps any VAT-eligible line items to their
   * counterpart variants, then triggers a single section refresh.
   *
   * @param {boolean} isExempt
   * @returns {Promise<void>}
   */
  async function swapCartVatVariants(isExempt) {
    const cartInstance = window.cart;
    if (!cartInstance) {
      console.warn('[VAT] window.cart is not available — cart swap skipped.');
      return;
    }

    const targetVatValue = isExempt ? VAT_EXEMPT_VALUE : VAT_STANDARD_VALUE;
    productCache.clear();

    // Update cart note with VAT exemption reason (always, regardless of cart contents)
    try {
      const reason = isExempt ? (localStorage.getItem(VAT_REASON_KEY) || '') : '';
      const note = isExempt ? ('VAT Exempt' + (reason ? ': ' + reason : '')) : '';
      await cartInstance.request('update', { note: note }, { includeSections: false, requestName: 'vat-note' });
    } catch (err) {
      console.warn('[VAT] Failed to update cart note:', err);
    }

    // Fetch the current cart state silently
    let currentCart;
    try {
      currentCart = await cartInstance.get();
    } catch (err) {
      console.error('[VAT] cart.get() threw an error:', err);
      return;
    }

    const items = (currentCart && currentCart.items) ? currentCart.items : [];
    if (!items.length) {
      return;
    }

    // Resolve which items need swapping
    const swaps = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      try {
        const product = await fetchProduct(item.handle);
        const counterpart = findCounterpartVariant(product, item.variant_id, targetVatValue);
        if (counterpart) {
          swaps.push({ item: item, counterpart: counterpart });
        }
      } catch (err) {
        console.warn('[VAT] Could not resolve counterpart for "' + item.handle + '":', err);
      }
    }

    if (!swaps.length) {
      return;
    }

    for (let i = 0; i < swaps.length; i++) {
      const swap = swaps[i];
      try {
        await cartInstance.change(
          { id: swap.item.key, quantity: 0 },
          { includeSections: false, requestName: 'vat-remove' }
        );

        const newProperties = Object.assign({}, swap.item.properties || {});
        if (targetVatValue === VAT_EXEMPT_VALUE) {
          newProperties[' '] = 'VAT Exempt';
        } else {
          delete newProperties[' '];
        }

        const addPayload = {
          id: swap.counterpart.id,
          quantity: swap.item.quantity
        };
        if (Object.keys(newProperties).length) {
          addPayload.properties = newProperties;
        }
        // Auto-renew rental lines carry a selling plan (subscription). Each
        // VAT-Exemption variant has its OWN selling plan (Auto-renew/No and
        // Auto-renew/Yes are set up as separate plans in the Fire Rentals
        // app) — reusing the OLD line item's plan id against the NEW variant
        // fails with "Cannot apply selling plan to variant", so this reads
        // the plan off the counterpart variant we're actually adding.
        const counterpartSellingPlanId = sellingPlanIdFromAllocation(
          swap.counterpart.selling_plan_allocations && swap.counterpart.selling_plan_allocations[0]
        );
        if (counterpartSellingPlanId != null) {
          addPayload.selling_plan = counterpartSellingPlanId;
        }

        await cartInstance.request(
          'add',
          addPayload,
          { includeSections: false, requestName: 'vat-add' }
        );
      } catch (err) {
        console.error('[VAT] Swap failed for item "' + swap.item.key + '":', err);
      }
    }

    const sections = cartInstance.getDefaultSections({ includeRecommendations: false });
    await cartInstance.refreshSections(sections, { reason: 'vat-swap' });
  }

  /**
   * VAT exemption state — read/write localStorage and trigger a cart swap
   * whenever the value changes.
   */
  window.vatExemption = {
    /** @returns {boolean} */
    isExempt: function () {
      return localStorage.getItem(VAT_STORAGE_KEY) === 'true';
    },

    /**
     * Persists the new exemption state and swaps the cart if the value changed.
     * @param {boolean} value
     * @returns {Promise<void>}
     */
    set: async function (value) {
      localStorage.setItem(VAT_STORAGE_KEY, String(value));
      await swapCartVatVariants(value);
    },
  };

})();
